import { Message } from '@twilio/conversations';
import classNames from 'classnames';
import i18next from 'i18next';

import { useTranslate } from '@growtherapy/i18n';
import { AvatarAccent, InitialsAvatar, Text, Tooltip, cn } from '@growtherapy/sprout-ui';

import { useTheme } from '../../hooks/useTheme';
import { Theme } from '../../state/theme';
import { nameToInitials } from '../../utils';
import { parseIdentity } from '../../video-sdk/core/room/participant/utils';
import { detectLinks } from '../utils';

export function ChatBubble({ message, isSelf }: { message: Message; isSelf: boolean }) {
  const t = useTranslate();
  const { theme } = useTheme();
  const isDarkTheme = theme === Theme.DARK;
  const displayName = parseIdentity(message.author).name ?? t('telehealth-core:participantInfo.participant');

  return (
    <li
      className='mb-4 flex gap-3'
      aria-label={
        isSelf
          ? t('telehealth-core:chatDrawer.messageFromYou')
          : t('telehealth-core:chatDrawer.messageFromName', {
              name: displayName,
            })
      }
    >
      {!isSelf && (
        <Tooltip text={displayName} childIsInteractive={false} data-dd-privacy='mask' className='fs-exclude'>
          <InitialsAvatar aria-hidden size='xs' initials={nameToInitials(displayName)} accent={AvatarAccent.Lilac} />
        </Tooltip>
      )}
      <div
        className={classNames(
          {
            'max-w-[calc(100%-3rem)]': !isSelf,
            'max-w-full ml-auto': isSelf,
          },
          'flex flex-col',
        )}
      >
        <Text
          variant='sm'
          data-dd-privacy='mask'
          data-dd-action-name='Click on message'
          className={classNames(
            {
              'bg-lilac-700 text-neutral-100 [&>a]:text-neutral-100 rebrand:bg-surface-inverse rebrand:text-text-inverse rebrand:[&>a]:text-text-inverse':
                !isSelf,
              'text-neutral-900 ml-auto bg-neutral-200 rebrand:bg-surface-state-inactive-emphasis rebrand:text-text-default':
                isSelf,
            },
            'rounded-xl px-4 py-3 fs-exclude [&>a]:underline max-w-full break-words',
          )}
        >
          {detectLinks(message.body)}
        </Text>
        <Text
          variant='xs'
          className={cn('text-neutral-700 ml-auto pt-1 rebrand:text-text-default', {
            'rebrand:text-text-inverse': isDarkTheme,
          })}
        >
          <span className='sr-only'>{t('telehealth-core:chatDrawer.sentAt')}&nbsp;</span>
          {message.dateCreated?.toLocaleTimeString(i18next.language, {
            hour: 'numeric',
            minute: 'numeric',
          })}
        </Text>
      </div>
    </li>
  );
}
